import { Control, DomUtil, Map, LatLng, DomEvent, ControlOptions, Polyline, CircleMarker } from 'leaflet';
import { EventEmitter } from '@angular/core';

export class PolygonSelectionControl extends Control {
	private container!: HTMLDivElement;
	private confirmButton!: HTMLButtonElement;
	private cancelButton!: HTMLButtonElement;
	private titleRow!: HTMLDivElement;
	private isSelecting = false;
	private vertices: LatLng[] = [];
	private polyline: Polyline | null = null;
	private vertexMarkers: CircleMarker[] = [];
	private allControls: HTMLElement[] = [];
	private map: Map | null = null;

	// Mindestabstand zum ersten Punkt um Polygon zu schließen (in Pixeln)
	private readonly CLOSE_THRESHOLD = 20;

	// EventEmitter für Angular
	public polygonSelected = new EventEmitter<LatLng[]>();
	public selectionCancelled = new EventEmitter<void>();

	constructor(options?: ControlOptions) {
		super(options);
	}

	override onAdd(map: Map): HTMLElement {
		this.map = map;
		this.container = DomUtil.create('div', 'leaflet-control leaflet-selection-control');

		this.titleRow = DomUtil.create('div', 'selection-title', this.container);
		this.titleRow.innerHTML = '<strong>Polygon zeichnen</strong>';
		this.titleRow.style.display = 'none';

		const buttonRow = DomUtil.create('div', 'selection-buttons', this.container);

		// Confirm Button (initial versteckt)
		this.confirmButton = DomUtil.create('button', 'btn btn-success btn-sm', buttonRow);
		this.confirmButton.innerHTML = '<i class="bi bi-check-lg"></i>';
		this.confirmButton.title = 'Polygon abschließen';
		this.confirmButton.style.display = 'none';
		DomEvent.on(this.confirmButton, 'click', this.confirmPolygon, this);

		// Cancel Button (initial versteckt)
		this.cancelButton = DomUtil.create('button', 'btn btn-danger btn-sm', buttonRow);
		this.cancelButton.innerHTML = '<i class="bi bi-x-lg"></i>';
		this.cancelButton.title = 'Abbrechen';
		this.cancelButton.style.display = 'none';
		DomEvent.on(this.cancelButton, 'click', this.cancelSelection, this);

		// Verhindere Map-Events auf den Buttons
		DomEvent.disableClickPropagation(this.container);
		DomEvent.disableScrollPropagation(this.container);

		return this.container;
	}

	override onRemove(): void {
		DomEvent.off(this.confirmButton, 'click', this.confirmPolygon, this);
		DomEvent.off(this.cancelButton, 'click', this.cancelSelection, this);
		this.map = null;
	}

	public startSelection(actionText?: string): void {
		if (!this.map || this.isSelecting) return;

		this.isSelecting = true;
		this.vertices = [];
		this.container.classList.add('selecting');

		// Eigenes Pane für Zeichnungs-Layer erstellen (on-top)
		if (!this.map.getPane('polygonDrawPane')) {
			const pane = this.map.createPane('polygonDrawPane');
			pane.style.zIndex = '650'; // Über markerPane (600) und shadowPane (500)
		}

		// Alle Controls ausblenden (außer diesem)
		this.hideAllControls();

		if (actionText) {
			this.titleRow.innerHTML = actionText;
		} else {
			this.titleRow.innerHTML = 'Klicken Sie auf die Karte um Eckpunkte zu setzen';
		}
		this.titleRow.style.display = 'block';

		// Buttons anzeigen
		this.cancelButton.style.display = 'block';
		// Confirm-Button erst ab 3 Punkten sichtbar
		this.confirmButton.style.display = 'none';

		// Cursor ändern
		this.map.getContainer().style.cursor = 'crosshair';

		// Touch-Interaktion konfigurieren
		if (this.map.dragging) {
			this.map.dragging.disable();
		}

		// Tap-Handler deaktivieren
		if ((this.map as any).tap) {
			(this.map as any).tap.disable();
		}

		// Click Events registrieren
		this.map.on('click', this.onMapClick, this);
	}

	private onMapClick(e: any): void {
		if (!this.map || !this.isSelecting) return;

		const clickedLatLng = e.latlng as LatLng;

		// Prüfen ob Klick nahe am ersten Punkt ist (Polygon schließen)
		if (this.vertices.length >= 3) {
			const firstPoint = this.map.latLngToContainerPoint(this.vertices[0]);
			const clickPoint = this.map.latLngToContainerPoint(clickedLatLng);
			const distance = Math.sqrt(
				Math.pow(firstPoint.x - clickPoint.x, 2) +
				Math.pow(firstPoint.y - clickPoint.y, 2)
			);

			if (distance <= this.CLOSE_THRESHOLD) {
				// Polygon schließen
				this.confirmPolygon();
				return;
			}
		}

		// Neuen Vertex hinzufügen
		this.vertices.push(clickedLatLng);

		// Polyline aktualisieren
		this.updatePolyline();

		// Vertex-Marker hinzufügen
		this.addVertexMarker(clickedLatLng, this.vertices.length === 1);

		// Confirm-Button ab 3 Punkten anzeigen
		if (this.vertices.length >= 3) {
			this.confirmButton.style.display = 'block';
		}

		// Anleitung aktualisieren
		this.updateInstructions();
	}

	private updatePolyline(): void {
		if (!this.map) return;

		// Alte Polyline entfernen
		if (this.polyline) {
			this.polyline.remove();
		}

		if (this.vertices.length < 2) return;

		// Neue Polyline erstellen (mit Verbindung zum Startpunkt als gestrichelte Linie)
		const points = [...this.vertices];
		if (this.vertices.length >= 3) {
			points.push(this.vertices[0]); // Schließe zum Startpunkt
		}

		this.polyline = new Polyline(points, {
			color: '#3388ff',
			weight: 2,
			dashArray: '5, 5',
			fillOpacity: 0.1,
			pane: 'polygonDrawPane'
		}).addTo(this.map);
	}

	private addVertexMarker(latlng: LatLng, isFirst: boolean): void {
		if (!this.map) return;

		const marker = new CircleMarker(latlng, {
			radius: isFirst ? 8 : 5,
			color: isFirst ? '#28a745' : '#3388ff',
			fillColor: isFirst ? '#28a745' : '#3388ff',
			fillOpacity: 1,
			weight: 2,
			pane: 'polygonDrawPane'
		}).addTo(this.map);

		if (isFirst && this.vertices.length >= 3) {
			// Tooltip für ersten Punkt
			marker.bindTooltip('Klicken zum Schließen', {
				permanent: false,
				direction: 'top'
			});
		}

		this.vertexMarkers.push(marker);
	}

	private updateInstructions(): void {
		const count = this.vertices.length;
		if (count === 0) {
			this.titleRow.innerHTML = 'Klicken Sie auf die Karte um Eckpunkte zu setzen';
		} else if (count === 1) {
			this.titleRow.innerHTML = 'Weiteren Punkt setzen...';
		} else if (count === 2) {
			this.titleRow.innerHTML = 'Mindestens noch einen Punkt setzen';
		} else {
			this.titleRow.innerHTML = `${count} Punkte gesetzt. Zum Schließen auf ersten Punkt klicken oder ✓`;
		}
	}

	private confirmPolygon(): void {
		if (!this.map || this.vertices.length < 3) return;

		// Emit das Polygon
		this.polygonSelected.emit([...this.vertices]);

		// Cleanup
		this.finishSelection();
	}

	private cancelSelection(): void {
		this.finishSelection();
		this.selectionCancelled.emit();
	}

	private finishSelection(): void {
		if (!this.map) return;

		this.isSelecting = false;
		this.container.classList.remove('selecting');

		// Polyline entfernen
		if (this.polyline) {
			this.polyline.remove();
			this.polyline = null;
		}

		// Vertex-Marker entfernen
		this.vertexMarkers.forEach(m => m.remove());
		this.vertexMarkers = [];
		this.vertices = [];

		// Events entfernen
		this.map.off('click', this.onMapClick, this);

		// Cursor zurücksetzen
		this.map.getContainer().style.cursor = '';

		// Touch-Interaktion wiederherstellen
		if (this.map.dragging) {
			this.map.dragging.enable();
		}

		if ((this.map as any).tap) {
			(this.map as any).tap.enable();
		}

		// Controls wieder einblenden
		this.showAllControls();

		// Buttons verstecken
		this.titleRow.style.display = 'none';
		this.confirmButton.style.display = 'none';
		this.cancelButton.style.display = 'none';
	}

	private hideAllControls(): void {
		if (!this.map) return;

		const mapContainer = this.map.getContainer();
		const controls = mapContainer.querySelectorAll('.leaflet-control');

		this.allControls = [];

		controls.forEach((control) => {
			const htmlControl = control as HTMLElement;
			// Nicht dieses Control verstecken
			if (htmlControl !== this.container && htmlControl.style.display !== 'none') {
				this.allControls.push(htmlControl);
				htmlControl.style.display = 'none';
			}
		});
	}

	private showAllControls(): void {
		this.allControls.forEach((control) => {
			control.style.display = '';
		});
		this.allControls = [];
	}
}
